import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@askable/ui/core/dialog';
import noop from 'lodash/noop';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwitchTeam } from 'src/hooks/useSwitchTeam';

import { InviteTeammate } from 'components/common';
import { CreditPurchasingDisabledModal } from 'components/common/Askable/BuyCredits/CreditPurchasingDisabledModal';
import { useConnectedClient } from 'context/ConnectedClientContext';

// Create a new context for the app
export type AppContextType = {
  onSwitchToDifferentTeam: ReturnType<typeof useSwitchTeam>['switchTeam'];
  onOpenInviteTeamMemberModal: (callback?: null | (() => void)) => void;
  // Self-serve credit purchasing is retired, so this only explains why and offers a demo.
  onOpenBuyCreditsModal: () => void;
  onUpdateLoading: () => void;
};

export const AppContext = React.createContext<AppContextType | undefined>(undefined);
export function useAppContext() {
  const ctx = useContext(AppContext);

  if (ctx === undefined) {
    throw new Error('useAppContext cannot be used outside of AppProvider');
  }

  return ctx;
}

// Creates a provider Component
export function AppProvider(props: any) {
  const { t } = useTranslation();
  const connectedClient = useConnectedClient();

  const [inviteTeamMember, setInviteTeamMember] = useState({
    open: false,
    callback: noop,
  });
  const [openBuyCreditsModal, setOpenBuyCreditsModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const { switchTeam } = useSwitchTeam({
    updateClientDetails: connectedClient.actions.updateClientDetails,
  });

  const onOpenInviteTeamMemberModal = (callback?: null | (() => void)) => {
    setInviteTeamMember({
      open: true,
      callback: callback || noop,
    });
  };

  const onCloseBuyCreditsModal = () => {
    setOpenBuyCreditsModal(false);
  };

  const onOpenBuyCreditsModal = () => {
    setOpenBuyCreditsModal(true);
  };

  const onUpdateLoading = () => {
    setLoading(!loading);
  };

  const onSwitchToDifferentTeam = async (id: string) => {
    return switchTeam(id);
  };

  return (
    <AppContext.Provider
      value={{
        onOpenInviteTeamMemberModal,
        onOpenBuyCreditsModal,
        onUpdateLoading,
        onSwitchToDifferentTeam,
      }}
    >
      {props.children}
      <Dialog
        open={inviteTeamMember.open}
        onOpenChange={open => {
          if (!open) {
            setInviteTeamMember({
              open: false,
              callback: noop,
            });
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {t('sections.settings.team.invitePeopleTo', { teamName: connectedClient?.details?.team?.name })}
            </DialogTitle>
          </DialogHeader>
          <InviteTeammate
            title={null}
            onClose={() => {
              setInviteTeamMember({
                open: false,
                callback: noop,
              });
            }}
            onDone={() => inviteTeamMember.callback && inviteTeamMember.callback()}
            showCloseIcon
          />
        </DialogContent>
      </Dialog>
      <CreditPurchasingDisabledModal open={openBuyCreditsModal} onClose={onCloseBuyCreditsModal} />
    </AppContext.Provider>
  );
}
